import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

// Reply card component
function ReplyCard({ reply, onAction, isSelected, onSelect }) {
  const [actionLoading, setActionLoading] = useState(false);

  const handleAction = async (action, params = {}) => {
    setActionLoading(true);
    await onAction(reply.id, action, params);
    setActionLoading(false);
  };

  return (
    <div 
      className={`bg-white dark:bg-slate-800 rounded-xl border p-4 transition-all cursor-pointer ${
        isSelected ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200 dark:border-slate-700 hover:shadow-md'
      } ${!reply.read ? 'border-l-4 border-l-blue-500' : ''}`}
      onClick={() => onSelect(reply)}
      data-testid={`reply-card-${reply.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-semibold truncate ${!reply.read ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
              {reply.lead_name || 'Unknown Lead'}
            </span>
            {!reply.read && (
              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></span>
            )}
          </div>
          
          {/* Email and Company */}
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
            {reply.lead_email} {reply.lead_organization && `• ${reply.lead_organization}`}
          </p>
          
          {/* Subject */}
          <p className="text-sm text-slate-800 dark:text-slate-100 font-medium mt-2 truncate">
            {reply.subject || '(No Subject)'}
          </p>
          
          {/* Body Preview */}
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
            {reply.body?.substring(0, 150)}...
          </p>
          
          {/* Meta Info */}
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {reply.sequence_name}
            </span>
            <span>
              {reply.received_at ? format(parseISO(reply.received_at), 'MMM d, HH:mm') : 'N/A'}
            </span>
            {reply.deal_value && (
              <span className="text-green-600 font-medium">
                ₹{reply.deal_value.toLocaleString('en-IN')}
              </span>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-col gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleAction('mark_read'); }}
            disabled={actionLoading || reply.read}
            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-30"
            title="Mark as read"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleAction('create_task'); }}
            disabled={actionLoading}
            className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
            title="Create task"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleAction('archive'); }}
            disabled={actionLoading}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
            title="Archive"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// Reply detail panel
function ReplyDetailPanel({ reply, onClose, onAction }) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    if (reply) {
      setReplySubject(`Re: ${reply.subject || ''}`);
    }
  }, [reply]);

  const handleSendReply = async () => {
    if (!replyBody.trim()) {
      alert('Please enter a message');
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/replies/${reply.id}/send-reply`, null, {
        params: { subject: replySubject, body: replyBody }
      });
      setShowReplyForm(false);
      setReplyBody('');
      alert('Reply sent successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleMoveToStage = async () => {
    if (!newStatus) return;
    await onAction(reply.id, 'move_to_stage', { new_status: newStatus });
    setNewStatus('');
  };

  if (!reply) return null;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-white">{reply.lead_name}</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">{reply.lead_email}</p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Lead Quick Actions */}
      <div className="p-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to={`/leads/${reply.lead_id}`}
            className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200"
          >
            View Lead
          </Link>
          <div className="flex items-center gap-2">
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded-lg"
            >
              <option value="">Move to stage...</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="meeting">Meeting Set</option>
              <option value="proposal">Proposal</option>
              <option value="closed_won">Closed Won</option>
            </select>
            {newStatus && (
              <button
                onClick={handleMoveToStage}
                className="px-2 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-slate-800 dark:hover:bg-white"
              >
                Move
              </button>
            )}
          </div>
          {reply.deal_value && (
            <span className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded-lg">
              Deal: ₹{reply.deal_value.toLocaleString('en-IN')}
            </span>
          )}
        </div>
      </div>

      {/* Email Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mb-4">
          <p className="text-lg font-medium text-slate-900 dark:text-white">{reply.subject}</p>
          <p className="text-xs text-slate-400 mt-1">
            Received {reply.received_at ? format(parseISO(reply.received_at), 'MMM d, yyyy HH:mm') : 'N/A'}
            {reply.sequence_name && ` • From sequence: ${reply.sequence_name}`}
          </p>
        </div>
        
        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
          {reply.body}
        </div>

        {/* Email History */}
        {reply.email_history && reply.email_history.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Email Thread</h4>
            <div className="space-y-3">
              {reply.email_history.map((email, idx) => (
                <div key={email.id || idx} className="bg-blue-50 rounded-lg p-3 border-l-2 border-blue-400">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-blue-700">Step {email.step_index + 1}</span>
                    <span className="text-xs text-slate-400">
                      {email.sent_at ? format(parseISO(email.sent_at), 'MMM d, HH:mm') : 'N/A'}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{email.subject}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{email.body?.substring(0, 100)}...</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reply Form */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-700">
        {!showReplyForm ? (
          <button
            onClick={() => setShowReplyForm(true)}
            className="w-full py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
            Reply
          </button>
        ) : (
          <div className="space-y-3">
            <input
              type="text"
              value={replySubject}
              onChange={(e) => setReplySubject(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
              placeholder="Subject"
            />
            <textarea
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
              placeholder="Type your reply..."
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowReplyForm(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSendReply}
                disabled={sending}
                className="flex-1 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-50"
              >
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Main Reply Inbox Page
export default function ReplyInboxPage() {
  const [replies, setReplies] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedReply, setSelectedReply] = useState(null);
  const [detailReply, setDetailReply] = useState(null);
  const [filter, setFilter] = useState('unread'); // unread, all

  const fetchReplies = useCallback(async () => {
    try {
      const [repliesRes, statsRes] = await Promise.all([
        axios.get(`${API}/replies`, { params: { unread_only: filter === 'unread' } }),
        axios.get(`${API}/replies/stats`)
      ]);
      setReplies(repliesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching replies:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchReplies();
  }, [fetchReplies]);

  const handleSelectReply = async (reply) => {
    setSelectedReply(reply);
    
    // Fetch full reply details
    try {
      const response = await axios.get(`${API}/replies/${reply.id}`);
      setDetailReply(response.data);
    } catch (error) {
      console.error('Error fetching reply details:', error);
    }
  };

  const handleAction = async (replyId, action, params = {}) => {
    try {
      await axios.post(`${API}/replies/${replyId}/action`, { action, ...params });
      fetchReplies();
      
      if (selectedReply?.id === replyId && action === 'archive') {
        setSelectedReply(null);
        setDetailReply(null);
      }
    } catch (error) {
      console.error('Error performing action:', error);
      alert(error.response?.data?.detail || 'Action failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)]" data-testid="reply-inbox-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Reply Inbox</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Replies to your email sequences</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-1">
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === 'unread' ? 'bg-blue-100 text-blue-700' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              Unread ({stats.unread})
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === 'all' ? 'bg-blue-100 text-blue-700' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              All ({stats.total})
            </button>
          </div>
          <Link
            to="/sequences"
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900"
          >
            Back to Sequences
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex gap-6 h-full">
        {/* Replies List */}
        <div className={`${selectedReply ? 'w-1/2' : 'w-full'} space-y-3 overflow-y-auto pr-2`}>
          {replies.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
              <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">No replies yet</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {filter === 'unread' ? 'All caught up! No unread replies.' : 'No replies have been received yet.'}
              </p>
            </div>
          ) : (
            replies.map(reply => (
              <ReplyCard
                key={reply.id}
                reply={reply}
                isSelected={selectedReply?.id === reply.id}
                onSelect={handleSelectReply}
                onAction={handleAction}
              />
            ))
          )}
        </div>

        {/* Detail Panel */}
        {selectedReply && (
          <div className="w-1/2 h-full">
            <ReplyDetailPanel
              reply={detailReply || selectedReply}
              onClose={() => { setSelectedReply(null); setDetailReply(null); }}
              onAction={handleAction}
            />
          </div>
        )}
      </div>
    </div>
  );
}
